import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import AppliedFilterChip, {
  type AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';

const t = withLocalePrefix('talent_matching.filters.applied');

type Props = {
  formDefaults: Record<string, unknown>;
};

type FormValues = {
  can_have: string[];
  must_have: string[];
  does_not_have: string[];
  milestone: Item[] | null;
  stages: Item[] | null;
  rejection_reasons: string[];
  scorecard_performance: Item[] | null;
  no_strong_no_scores: boolean;
  exclude_hired: boolean;
  candidate_tags: Item[];
  last_activity_start: string;
  last_activity_end: string;
  referrals_only: boolean;
  schools: Item[];
  degrees: Item[];
  disciplines: Item[];
  jobs: Item[];
  departments: Item[];
  offices: Item[];
  application_fields: unknown[];
  custom_fields: unknown[];
};

const FORM_FIELD_KEYS: (keyof FormValues)[] = [
  'can_have',
  'must_have',
  'does_not_have',
  'milestone',
  'stages',
  'rejection_reasons',
  'scorecard_performance',
  'no_strong_no_scores',
  'exclude_hired',
  'candidate_tags',
  'last_activity_start',
  'last_activity_end',
  'referrals_only',
  'schools',
  'degrees',
  'disciplines',
  'jobs',
  'departments',
  'offices',
  'application_fields',
  'custom_fields',
];

function itemLabels(items: Item[]): string {
  return items.map((i) => i.label).join(', ');
}

function buildFilters(
  values: FormValues
): Omit<AppliedFilterChipProps, 'formDefaults'>[] {
  const filters: Omit<AppliedFilterChipProps, 'formDefaults'>[] = [];

  if (values.can_have?.length > 0) {
    filters.push({
      label: t('keywords.can_have', { keywords: values.can_have.join(', ') }),
      filterKeys: ['can_have'],
      uniqueKey: 'keywords.can_have',
    });
  }

  if (values.must_have?.length > 0) {
    filters.push({
      label: t('keywords.must_have', { keywords: values.must_have.join(', ') }),
      filterKeys: ['must_have'],
      uniqueKey: 'keywords.must_have',
    });
  }

  if (values.does_not_have?.length > 0) {
    filters.push({
      label: t('keywords.does_not_have', {
        keywords: values.does_not_have.join(', '),
      }),
      filterKeys: ['does_not_have'],
      uniqueKey: 'keywords.does_not_have',
    });
  }

  if (values.milestone?.length) {
    filters.push({
      label: t('milestone', { milestones: itemLabels(values.milestone) }),
      filterKeys: ['milestone'],
      uniqueKey: 'milestone',
    });
  }

  if (values.stages?.length) {
    filters.push({
      label: t('stages', { stages: itemLabels(values.stages) }),
      filterKeys: ['stages'],
      uniqueKey: 'stages',
    });
  }

  if (values.rejection_reasons?.length > 0) {
    filters.push({
      label: t('rejection_reasons', {
        reasons: values.rejection_reasons.join(', '),
      }),
      filterKeys: ['rejection_reasons'],
      uniqueKey: 'rejection_reasons',
    });
  }

  if (values.scorecard_performance?.length) {
    filters.push({
      label: t('scorecard_performance', {
        performance: itemLabels(values.scorecard_performance),
      }),
      filterKeys: ['scorecard_performance'],
      uniqueKey: 'scorecard_performance',
    });
  }

  if (values.no_strong_no_scores) {
    filters.push({
      label: t('no_strong_no_scores'),
      filterKeys: ['no_strong_no_scores'],
      uniqueKey: 'no_strong_no_scores',
    });
  }

  if (values.exclude_hired) {
    filters.push({
      label: t('exclude_hired'),
      filterKeys: ['exclude_hired'],
      uniqueKey: 'exclude_hired',
    });
  }

  if (values.candidate_tags?.length > 0) {
    filters.push({
      label: t('candidate_tags', { tags: itemLabels(values.candidate_tags) }),
      filterKeys: ['candidate_tags'],
      uniqueKey: 'candidate_tags',
    });
  }

  if (values.last_activity_start || values.last_activity_end) {
    filters.push({
      label: t('last_activity', {
        start: values.last_activity_start || '...',
        end: values.last_activity_end || '...',
      }),
      filterKeys: ['last_activity_start', 'last_activity_end'],
      uniqueKey: 'last_activity',
    });
  }

  if (values.referrals_only) {
    filters.push({
      label: t('referrals_only'),
      filterKeys: ['referrals_only'],
      uniqueKey: 'referrals_only',
    });
  }

  if (values.schools?.length > 0) {
    filters.push({
      label: t('schools', { schools: itemLabels(values.schools) }),
      filterKeys: ['schools'],
      uniqueKey: 'schools',
    });
  }

  if (values.degrees?.length > 0) {
    filters.push({
      label: t('degrees', { degrees: itemLabels(values.degrees) }),
      filterKeys: ['degrees'],
      uniqueKey: 'degrees',
    });
  }

  if (values.disciplines?.length > 0) {
    filters.push({
      label: t('disciplines', { disciplines: itemLabels(values.disciplines) }),
      filterKeys: ['disciplines'],
      uniqueKey: 'disciplines',
    });
  }

  if (values.jobs?.length > 0) {
    filters.push({
      label: t('jobs', { jobs: itemLabels(values.jobs) }),
      filterKeys: ['jobs'],
      uniqueKey: 'jobs',
    });
  }

  if (values.departments?.length > 0) {
    filters.push({
      label: t('departments', { departments: itemLabels(values.departments) }),
      filterKeys: ['departments'],
      uniqueKey: 'departments',
    });
  }

  if (values.offices?.length > 0) {
    filters.push({
      label: t('offices', { offices: itemLabels(values.offices) }),
      filterKeys: ['offices'],
      uniqueKey: 'offices',
    });
  }

  if ((values.application_fields as unknown[])?.length > 0) {
    filters.push({
      label: t('application_fields'),
      filterKeys: ['application_fields'],
      uniqueKey: 'application_fields',
    });
  }

  if ((values.custom_fields as unknown[])?.length > 0) {
    filters.push({
      label: t('custom_fields'),
      filterKeys: ['custom_fields'],
      uniqueKey: 'custom_fields',
    });
  }

  return filters;
}

const RediscoveryAppliedFilters = ({ formDefaults }: Props) => {
  const { getValues } = useFormContext();

  const rawValues = getValues(FORM_FIELD_KEYS);
  const values: FormValues = Object.fromEntries(
    FORM_FIELD_KEYS.map((key, i) => [key, rawValues[i]])
  ) as FormValues;

  const chips = buildFilters(values).map((filter) => (
    <AppliedFilterChip
      key={filter.uniqueKey}
      formDefaults={formDefaults}
      {...filter}
    />
  ));

  return <BaseAppliedFiltersRow chips={chips} />;
};

export default RediscoveryAppliedFilters;
